import {emailRegex, orgNoRegex} from '@/services/validators.service'
import {NewSubAccount, SubAccount} from '@/subaccount/types/SubAccount'
import {validateBban as bbanValidator, validateIban as ibanValidator, validateBic as bicValidator} from '@/bank/bank-account.service'
import {Maybe} from '@/types/Maybe'

const validateName = (name: Maybe<string>): string | undefined => {
    if (!name || name?.trim().length === 0) {
        return 'sub-account.validation.name.blank'
    }
}

const validateBlank = (value: Maybe<string>): string | undefined => {
    if (!value || value?.trim().length === 0) {
        return 'sub-account.validation.blank'
    }
}

const validateBlankOrEmail = (email: Maybe<string>): string | undefined => {
    if (email == '' || (!!email && !emailRegex.test(email))) {
        return 'sub-account.validation.email.invalid'
    }
}

const validateEmail = (email: Maybe<string>): string | undefined => {
    if (!!email && !emailRegex.test(email)) {
        return 'sub-account.validation.email.invalid'
    }
}

const validatePhoneNumber = (phoneNumber: Maybe<string>): string | undefined => {
    if (!phoneNumber && phoneNumber?.trim().length === 0) {
        return 'sub-account.validation.phone-number.blank'
    }
}

const validateOrgNo = (orgNo: Maybe<string>, countryCode: string | undefined, isBusiness: boolean = false): string | undefined => {
    if (isBusiness) {
        if (countryCode === 'NO') {
            if (!orgNo || orgNo.trim().length === 0) {
                return 'sub-account.validation.org-no.blank'
            }
            if (!orgNoRegex.test(orgNo)) {
                return 'sub-account.validation.org-no.invalid'
            }
        }
    } else {
        if (orgNo) {
            return 'sub-account.validation.org-no.unexpected'
        }
    }
}

const validatePostalCode = (postalCode: Maybe<string>): string | undefined => {
    if (!postalCode || postalCode?.trim().length === 0) {
        return 'sub-account.validation.address.postal-code.blank'
    }
}

const validatePostalLocation = (postalLocation: Maybe<string>): string | undefined => {
    if (!postalLocation || postalLocation?.trim().length === 0) {
        return 'sub-account.validation.address.postal-location.blank'
    }
}

const validateAddressLine = (addressLine: Maybe<string>): string | undefined => {
    if (!addressLine || addressLine?.trim().length === 0) {
        return 'sub-account.validation.address.address-line.blank'
    }
}

const validateCountryCode = (countryCode: Maybe<string>): string | undefined => {
    if (!countryCode || countryCode?.trim().length !== 2) {
        return 'sub-account.validation.address.country-code.blank'
    }
}

const validateIban = (iban: Maybe<string>): string | undefined => {
    if (iban && iban?.trim().length === 0) {
        return 'sub-account.validation.iban.blank'
    }
    if (iban && !ibanValidator(iban as string)) {
        return 'sub-account.validation.iban.invalid'
    }
}

const validateBban = (bban: Maybe<string>): string | undefined => {
    if (bban && bban?.trim().length === 0) {
        return 'sub-account.validation.bban.blank'
    }
    if (bban && !bbanValidator(bban as string)) {
        return 'sub-account.validation.bban.invalid'
    }
}

const validateBic = (bic: Maybe<string>): string | undefined => {
    if (bic && bic?.trim().length === 0) {
        return 'sub-account.validation.bic.blank'
    }
    if (bic && !bicValidator(bic as string)) {
        return 'sub-account.validation.bic.invalid'
    }
}

const validateAddress = (address: Maybe<{
    addressLine?: string,
    postalCode?: string,
    postalLocation?: string,
    countryCode?: string
}>): string | undefined => {
    if (address) {
        const blankPostalCode = validatePostalCode(address.postalCode)
        const blankPostalLocation = validatePostalLocation(address.postalLocation)
        const blankAddressLine = validateAddressLine(address.addressLine)
        const blankCountryCode = validateCountryCode(address.countryCode)
        if (!blankPostalCode || !blankPostalLocation || !blankAddressLine || !blankCountryCode) {
            if (blankPostalCode) {
                return blankPostalCode
            } else if (blankPostalLocation) {
                return blankPostalLocation
            } else if (blankAddressLine) {
                return blankAddressLine
            } else if (blankCountryCode) {
                return blankCountryCode
            }
        }
    }
}

const validateContactInfo = (contactInfo: Maybe<{
    name?: string,
    email?: string,
    phoneNumber?: string
}>, isBusiness?: boolean): string | undefined => {

    if (contactInfo) {
        const blankName = isBusiness ? validateBlank(contactInfo.name) : undefined
        const blankEmail = validateBlank(contactInfo.email)
        const blankPhoneNumber = validatePhoneNumber(contactInfo.phoneNumber)
        if (!blankName || !blankEmail || !blankPhoneNumber) {
            if (blankName) {
                return blankName
            }
        }
    }
}

const isValid = (subAccount: Maybe<SubAccount> | Maybe<NewSubAccount>): boolean => {
    // return SubAccountSchema.safeParse(subAccount).success
    return !validateName(subAccount?.name) &&
    !validateContactInfo(subAccount?.contactInfo, subAccount?.isBusiness) &&
        !validateOrgNo(subAccount?.orgNo, subAccount?.address?.countryCode, subAccount?.isBusiness) &&
        !validateAddress(subAccount?.address) &&
        !validateIban(subAccount?.iban) &&
        !validateBban(subAccount?.bban) &&
        !validateBic(subAccount?.bic)
}

export const useSubAccountValidation = () => ({
    validateName,
    validateEmail,
    validateAddress,
    validateOrgNo,
    isValid,
    validatePostalCode,
    validatePostalLocation,
    validateAddressLine,
    validateCountryCode,
    validateIban,
    validateBban,
    validateBic,
    validateBlankOrEmail
})